import { useState } from "react";
import { ChevronDown, Clock, Truck, ShieldCheck } from "lucide-react";

const GROUPS = [
  {
    title: "Products",
    items: [
      {
        q: "Are these products safe for human use?",
        a: "No. All Century Labs compounds are supplied strictly for laboratory and research use only. They are not for human or animal consumption and are not intended to diagnose, treat, cure, or prevent any disease. By purchasing, you confirm you are a qualified researcher and accept sole responsibility for lawful, compliant use within your jurisdiction.",
      },
      {
        q: "How is every batch tested?",
        a: "Every batch is verified by accredited, independent third party laboratories. Depending on the compound we use techniques including HPLC, LC-MS, GC-MS, FTIR, NMR and UV-Vis, testing for purity, content, sterility, and endotoxins. A certificate of analysis is tied to your exact lot number, and a copy of that COA is included with every order you receive.",
      },
      {
        q: "In what form do the peptides ship?",
        a: "Peptides ship as a lyophilized (freeze-dried) powder for maximum stability, and require reconstitution with a suitable diluent (such as bacteriostatic water) before use in research. Optimal storage is below minus 20 °C; refrigeration at 4 °C is suitable for up to ~2 years.",
      },
      {
        q: "Why does the powder or vial top look different?",
        a: "Minor variations in color, texture, cake appearance, or vial-top color can occur due to polymorphy, buffering requirements, or transit. These variations are aesthetic only and do not affect quality, potency, or purity.",
      },
    ],
  },
  {
    title: "Shipping",
    items: [
      {
        q: "How fast do orders ship?",
        a: "Orders are processed within 1 to 3 business days after payment clears. Standard shipping takes 3 to 6 business days; express takes 2 to 4 business days. High-volume periods and holidays may add processing time. All timeframes are estimates.",
      },
      {
        q: "Do you ship worldwide?",
        a: "Yes, shipping is available worldwide. A tracking number is emailed once your order ships, and tracking updates can take up to 24 hours to appear.",
      },
      {
        q: "My label is printed but there is no tracking update.",
        a: "This usually means your order is still being processed. Please allow 2 to 3 days for movement. If there is still no update after that, contact us and we'll investigate.",
      },
    ],
  },
  {
    title: "Orders & Payments",
    items: [
      {
        q: "What do the order statuses mean?",
        a: "Payment Pending, we are awaiting or verifying your payment. Processing, payment confirmed and your order is being prepared. Completed, dispatched, with tracking emailed to you. Cancelled, payment could not be verified.",
      },
      {
        q: "What payment methods do you accept?",
        a: "Card availability can vary with frequently-changing industry regulations. All supported payment methods are shown at checkout.",
      },
      {
        q: "What is your refund policy?",
        a: "All sales are final. Verified missing, damaged, or wrong items reported within 7 days of delivery are remedied with store credit only. Please double-check your shipping address at checkout, incorrect addresses are the responsibility of the buyer.",
      },
    ],
  },
  {
    title: "Policies",
    items: [
      {
        q: "Research-use & zero-tolerance policy",
        a: "Century Labs supplies materials exclusively for laboratory research. We actively screen orders and enforce a strict zero-tolerance policy for misuse or abuse of research materials, suspected misuse results in permanent denial of service. Our support is limited to inquiries about shipping, payments, and related matters; we do not assist with research-related tasks.",
      },
    ],
  },
];

export default function FAQ() {
  const [open, setOpen] = useState("0-0");

  return (
    <div className="bg-[#070B1C] min-h-screen" data-testid="faq-page">
      {/* Header */}
      <section className="border-b border-white/12">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-24">
          <p className="text-[10px] tracking-[0.35em] uppercase font-bold text-electric mb-5">
            Support · COA with every order
          </p>
          <h1 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter uppercase leading-none text-white">
            Frequently asked
            <br />
            questions
          </h1>
          <p className="text-base text-white/60 mt-6 max-w-2xl leading-relaxed">
            Quality is not an option, it's a promise. Every batch undergoes meticulous testing for purity, vacuum-seal
            integrity, and quantitative accuracy. Below are the answers researchers ask most.
          </p>
        </div>
      </section>

      {/* Hours strip */}
      <section className="border-b border-white/12 bg-electric text-black">
        <div className="max-w-[1100px] mx-auto px-5 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-3">
          <div className="flex items-start gap-3 px-4 py-6 border-b sm:border-b-0 sm:border-r border-black/20">
            <Truck size={18} strokeWidth={1.75} className="mt-0.5" />
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold">Shipping Hours</div>
              <div className="text-xs mt-1">Mon to Fri · 9 AM to 5 PM EST</div>
            </div>
          </div>
          <div className="flex items-start gap-3 px-4 py-6 lg:border-r border-black/20">
            <Clock size={18} strokeWidth={1.75} className="mt-0.5" />
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold">Support (7 Days)</div>
              <div className="text-xs mt-1">Mon to Fri · 9 AM to 5 PM EST · Sat to Sun ad hoc</div>
            </div>
          </div>
          <div className="flex items-start gap-3 px-4 py-6 border-t sm:border-t-0 border-black/20 sm:col-span-2 lg:col-span-1">
            <ShieldCheck size={18} strokeWidth={1.75} className="mt-0.5" />
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold">Research Use Only</div>
              <div className="text-xs mt-1">Not for human or animal consumption</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ groups */}
      <section className="max-w-[1100px] mx-auto px-5 lg:px-8 py-16 lg:py-20">
        {GROUPS.map((group, gi) => (
          <div key={group.title} className="mb-14 last:mb-0">
            <h2 className="font-display font-bold text-2xl sm:text-3xl tracking-tighter uppercase text-white mb-6">
              {group.title}
            </h2>
            <div className="border-t border-white/12">
              {group.items.map((item, ii) => {
                const key = `${gi}-${ii}`;
                const isOpen = open === key;
                return (
                  <div key={key} className="border-b border-white/12">
                    <button
                      onClick={() => setOpen(isOpen ? "" : key)}
                      className="w-full flex items-center justify-between gap-4 py-5 text-left text-white"
                      data-testid={`faq-item-${key}`}
                    >
                      <span className="font-display font-bold text-sm sm:text-base tracking-tight uppercase">
                        {item.q}
                      </span>
                      <ChevronDown
                        size={18}
                        className={`shrink-0 text-electric transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
                      />
                    </button>
                    {isOpen && (
                      <p className="pb-6 text-sm text-white/60 leading-relaxed max-w-3xl">{item.a}</p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}
